import { Flex } from "@chakra-ui/react"
import * as React from "react"
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaVideo,
  FaVideoSlash
} from "react-icons/fa"
import { IoCameraReverse } from "react-icons/io5"
import { useRecoilValue, useRecoilState } from "recoil"
import { SDKContext } from "../../../../context/SDKContext"
import { localUserIdAtom, usersSelector } from "../../../../recoil/atoms/users"
import LocalButton from "../../buttonsLocalView/_localButton"
import { MobileLocalButtonsProps } from "./mobile-local-buttons.interface"
import {
  settingsDevicesSelector,
  mobileCamerasAtom,
  microphonePermissionStateAtom,
  cameraPermissionStateAtom
} from "../../../../recoil/atoms/settings"
import { isBigPlayIconState } from "../../../../recoil/atoms/bigPlayIcon"
import { watchPartyScreenSelector } from "../../../../recoil/atoms/watchPartyScreen"
import { showNoVideoSubscribeScreenSelector } from "../../../../recoil/atoms/rtc"
import useDeviceManagerChecker from "../../../../hooks/useDeviceManagerChecker"
import { DevicePermissionState } from "../../../settings/settings.interfaces"

const UserType = window.MeetOnline?.UserType
const UserPermission = window.MeetOnline?.UserPermission

const _MobileLocalButtons: React.FC<MobileLocalButtonsProps> = ({
  users,
  isMainMobileScreen,
  isMobile
}) => {
  const LSRTC = window.LSRTC
  const [isBigPlayIcon] = useRecoilState<boolean>(isBigPlayIconState)
  const _localUserId = useRecoilValue(localUserIdAtom)
  const _localUser = useRecoilValue(usersSelector(_localUserId))
  const meetingClient = React.useContext(SDKContext)
  const isMediaDisabled = useRecoilValue(showNoVideoSubscribeScreenSelector)
  const { toggleLocalCamera, toggleLocalMicrophone } = useDeviceManagerChecker()

  /* const localUserTracks = meetingClient?.getMediaTracks(_localUserId) */
  const [, setSavedDevices] = useRecoilState(settingsDevicesSelector)

  const isButtonHidden = (p: number) => {
    if (
      _localUser?.type === UserType?.AUDIENCE ||
      _localUser?.type === UserType?.BROADCASTER
    ) {
      return (_localUser?.permissions & p) === 0
    }
    return false
  }

  const isWatchParty = useRecoilValue(watchPartyScreenSelector)
  const settingsDevices = useRecoilValue(settingsDevicesSelector)
  const microphonePermissionState = useRecoilValue(microphonePermissionStateAtom)
  const cameraPermissionState = useRecoilValue(cameraPermissionStateAtom)

  const [mobileCameras, setMobileCameras] = useRecoilState(mobileCamerasAtom)

  const handleSwitchCamera = async () => {
    const localUserTracks = await meetingClient?.getMediaTracks(_localUserId)
    const noHaveCameras =
      mobileCameras.front.id === "" || mobileCameras.back.id === ""
    if (noHaveCameras) {
      const devices = await LSRTC.getDevices()
      const cameras = devices.filter(function (device) {
        return device.kind === "videoinput"
      })

      const transformedCameras = cameras.map(cam => ({
        id: cam.deviceId,
        label: cam.label
      }))

      if (transformedCameras.length > 1) {
        setMobileCameras({
          front: transformedCameras[0],
          back: transformedCameras[1]
        })
      }

      localUserTracks?.videoTrack?.setDevice(transformedCameras[1]?.id)
      setSavedDevices({
        video: {
          id: transformedCameras[1]?.id,
          label: transformedCameras[1]?.label
        },
        audio: settingsDevices.audio
      })
    } else {
      const newCamera =
        mobileCameras.front?.id ===
        localUserTracks?.videoTrack?._config?.cameraId
          ? mobileCameras.back
          : mobileCameras.front

      localUserTracks?.videoTrack?.setDevice(newCamera.id)
      setSavedDevices({
        video: {
          id: newCamera.id,
          label: newCamera.label
        },
        audio: settingsDevices.audio
      })
    }
  }

  return (
    <Flex
      justifyContent="center"
      position="relative"
      size="md"
      isAttached
      variant="outline"
      w="100%"
      color="gray.500"
      zIndex={isMainMobileScreen ? "unset" : "1201"}
      gridRowGap="10px"
      marginBottom={
        isMainMobileScreen && isBigPlayIcon && (!isWatchParty ? "80px" : "30px")
      }
      marginTop={!isMobile && isMainMobileScreen && "10px"}
    >
      <LocalButton
        id="1"
        names={["Unmute", "Mute"]}
        icons={[<FaMicrophoneSlash />, <FaMicrophone />]}
        enabled={_localUser?.micEnabled}
        setter={() => !isMediaDisabled && toggleLocalMicrophone()}
        isHidden={isButtonHidden(UserPermission.canUseMic)}
        isVerticalButton={true}
        isMobile={true}
        isRoundButton={true}
        isWithoutTitle={true}
        users={users}
        isMainMobileScreen={isMainMobileScreen}
        isDisabled={isMediaDisabled}
        showWarningIcon={
          microphonePermissionState === DevicePermissionState.denied
        }
      />

      <LocalButton
        id="2"
        names={["Start", "Stop"]}
        icons={[<FaVideoSlash />, <FaVideo />]}
        enabled={_localUser?.camEnabled}
        setter={() => !isMediaDisabled && toggleLocalCamera()}
        isHidden={isButtonHidden(UserPermission.canUseCam)}
        isVerticalButton={true}
        isMobile={true}
        isRoundButton={true}
        isWithoutTitle={true}
        users={users}
        isMainMobileScreen={isMainMobileScreen}
        isDisabled={isMediaDisabled}
        showWarningIcon={cameraPermissionState === DevicePermissionState.denied}
      />

      <LocalButton
        id="3"
        names={["Reverse"]}
        icons={[<IoCameraReverse />]}
        enabled={_localUser?.camEnabled}
        isDisabled={!_localUser?.camEnabled}
        isHidden={isButtonHidden(UserPermission.canUseCam)}
        setter={() => _localUser?.camEnabled && handleSwitchCamera()}
        isVerticalButton={true}
        isMobile={true}
        isRoundButton={true}
        isWithoutTitle={true}
        users={users}
        isMainMobileScreen={isMainMobileScreen}
      />
    </Flex>
  )
}

export const MobileLocalButtons = React.memo(_MobileLocalButtons)
